import { useEffect, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Typography, useMediaQuery } from '@mui/material';

// project-imports
import NavGroup from './NavGroup';
import {
  House2, People, Buliding, Stickynote, Personalcard, DocumentFilter, CalendarTick, Category,
  Profile2User,
  Chart21,
  Setting2
} from 'iconsax-react';

import { useSelector } from 'store';
import useConfig from 'hooks/useConfig';
import { HORIZONTAL_MAX_ITEM } from 'config';
import { MenuOrientation } from 'config';
import { hasBothOfficeTypes } from '../../../officeTypeAccess';


// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

const icons = {
  home: House2,
  visitors: People,
  projects: Buliding,
  reports: Stickynote,
  Managers: Personalcard,
  UserManagement: DocumentFilter,
  Logs: CalendarTick,
  dashbaord: Category,
  customerlist: Profile2User,
  reporticon: Chart21,
  master: Setting2,
};

const allMenuItems = [
  {
    // Single Dashboard entry for every role — was previously 4 separate hardcoded-role duplicates
    // (Security/Admin/Sales Manager/Sales Head/GRE each pointing at their own now-removed dashboard
    // route). All of those routes now redirect to this one URL (see MainRoutes_VMS.js), so this
    // shows for anyone holding ANY 'dashboard' module permission instead of matching a role string.
    id: 'Dashboard',
    title: 'Dashboard',
    type: 'item',
    url: '/vms/dashboard/',
    icon: icons.dashbaord,
    module: 'dashboard',
    permission: [
      'total customer',
      'unique customer',
      're-visited customer',
      'expected customer',
      'cross selling',
      'visitor type',
      'visited by project',
      'walk-in form status',
      'feedback form status',
      'cross selling by project',
      'project based bhk preference',
      'project based feedback',
      'visitors attended by sm',
      'top sm - table',
      're-assigned list',
      'peak visiting hours',
      'sales manager performance',
      'tomorrow expected',
      'visitor interest status',
      "today's anticipated",
      'project wise performance'
    ]
  },
  {
    id: 'SalesManagerList',
    title: 'SalesManager',
    type: 'item',
    url: '/vms/salesHead/home',
    icon: icons.Managers,
    // role: ['Sales Head'],
  },
  {
    // Receptionist's dashboard isn't folded into the unified permission-gated 'Dashboard' entry
    // above (unconfirmed whether Receptionist's permission payload even has a 'dashboard' module
    // entry) — kept as its own role-gated item so Receptionist doesn't lose it.
    id: 'frontdesk',
    title: 'Dashboard',
    type: 'item',
    url: '/vms/dashboard',
    icon: icons.dashbaord,
    // role: ['Receptionist'],
    module: 'dashboard', permission: 'total visitor check-in',
  },
  {
    // "Front Desk" prefix only actually shown when the logged-in role also has Visitor Office
    // access (see bothOfficeTypesVisible below) — a Receptionist already knows they're in Front
    // Desk, so the plain "Summary" label stays for them.
    id: 'frontsummary',
    title: 'Summary',
    disambiguatedTitle: 'Front Desk Summary',
    type: 'item',
    url: '/vms/checkin/frontdesk/summary',
    icon: icons.reports,
    // role: ['Receptionist'],
    module: 'visitor/courier', permission: "summary",
  },
  {
    // Single shared entry for GRE and Sales Manager alike — gated on the customer/summary
    // permission itself rather than a hardcoded role, since both roles land on the same list now.
    id: 'VisitorList',
    title: 'Visitor Summary',
    type: 'item',
    url: '/vms/visitorlist',
    icon: icons.customerlist,
    module: 'customer',
    permission: 'summary',
  },

  // {
  //   id: 'Projects',
  //   title: 'Projects',
  //   type: 'item',
  //   url: '/vms/projects/home',
  //   icon: icons.projects,
  //   role: ['Admin',]
  //   // module: 'projects', permission: 'view',
  // },
  {
    // Sales Manager assignment (GRE/Sales Manager flow) — not office-type master data, so it keeps
    // its own distinct label rather than sharing the generic "Master" title with the Visitor
    // Office / Front Desk master-data groups below.
    id: 'mastervisitor',
    title: 'SM Management',
    type: 'collapse',
    icon: icons.master,
    // role: ['GRE'],
    module: 'sm active', permission: "assign summary",
    children: [
      {
        id: 'smassign',
        title: 'SM Assinged',
        type: 'item',
        url: '/vms/gre/smassign',
        module: 'sm active', permission: "assign summary",
        // role: ['GRE'],
      },

    ]
  },
  {
    // Visitor Office master data (Vendor/Interest). Only relabeled to "Visitor Office Master" when
    // the role also has Front Desk master access (bothOfficeTypesVisible) — otherwise it's the
    // only "Master" group this role sees, so the plain label stays.
    id: 'Mastervendor',
    title: 'Master',
    disambiguatedTitle: 'Visitor Office Master',
    type: 'collapse',
    icon: icons.visitors,
    // role: ['Admin'],
    module: 'vendor', permission: "summary",
    children: [
      {
        id: 'MasterVendor',
        title: 'Vendor',
        type: 'item',
        url: '/vms/master/vendor',
        module: 'vendor', permission: "summary",
        // role: ['Admin'],
      },
      {
        id: 'MasterInterest',
        title: 'Interest',
        type: 'item',
        url: '/vms/master/interest',
        module: 'interest', permission: "edit",
        // role: ['Admin'],
      },
    ]
  },
  {
    // Front Desk reports (Visitor/Department). Only relabeled to "Front Desk Reports" when the
    // role also has Visitor Office reports access (bothOfficeTypesVisible) — a Receptionist only
    // ever sees this one "Reports" group, so the plain label stays for them.
    id: 'reports',
    title: 'Reports',
    disambiguatedTitle: 'Front Desk Reports',
    type: 'collapse',
    icon: icons.reporticon,
    // role: ['Receptionist'],
    module: 'report', permission: "visitor",

    children: [
      {
        id: 'visitor',
        title: 'Visitor',
        type: 'item',
        url: '/vms/report/visitor',
        module: 'report', permission: "visitor",
        // role: ['Receptionist'],
      },
      // {
      //   id: 'location',
      //   title: 'Location',
      //   type: 'item',
      //   url: '/vms/report/location',
      //   // module: 'interest', permission: "summary",
      //   role: ['Receptionist'],
      // },
      {
        id: 'departmentreport',
        title: 'Department',
        type: 'item',
        url: '/vms/report/department',
        module: 'report', permission: "department",
        // role: ['Receptionist'],
      },
    ]
  },
  {
    // Front Desk master data (Department/Office/Employee/ID Proof/Purpose of Visit). Only
    // relabeled to "Front Desk Master" when the role also has Visitor Office master access
    // (bothOfficeTypesVisible) — a Receptionist already knows they're in Front Desk, so the plain
    // "Master" label stays for them.
    id: 'Masterdepart',
    title: 'Master',
    disambiguatedTitle: 'Front Desk Master',
    type: 'collapse',
    icon: icons.master,
    // role: ['Receptionist'],
    module: 'department', permission: "summary",
    children: [
      {
        id: 'department',
        title: 'Department',
        type: 'item',
        url: '/vms/master/departments',
        module: 'department', permission: "summary",
        // role: ['Receptionist'],
      },
      {
        id: 'office',
        title: 'Office',
        type: 'item',
        url: '/vms/master/office',
        module: 'office', permission: "summary",
        // role: ['Receptionist'],
      },
      {
        id: 'employee',
        title: 'Employee',
        type: 'item',
        url: '/vms/master/employees',
        module: 'employee', permission: "summary",
        // role: ['Receptionist'],
      },
      {
        id: 'idproof',
        title: 'ID Proof',
        type: 'item',
        url: '/vms/master/id-proof',
        module: 'id proof type', permission: "summary",
        // role: ['Receptionist'],
      },
      {
        id: 'purposeofvisit',
        title: 'Purpose of Visit',
        type: 'item',
        url: '/vms/master/purpose-of-visit',
        // role: ['Receptionist'],
        module: 'purpose of visit', permission: "summary",
      },

    ]
  },

  {
    // Visitor Office reports (Visitor History/Project/Cross Selling). Only relabeled to "Visitor
    // Office Reports" when the role also has Front Desk reports access (bothOfficeTypesVisible) —
    // otherwise it's the only "Reports" group this role sees, so the plain label stays.
    id: 'Reports',
    title: 'Reports',
    disambiguatedTitle: 'Visitor Office Reports',
    type: 'collapse',
    icon: icons.reporticon,
    // role: ['Admin', 'Sales Manager', 'Sales Head', 'GRE'],
    module: 'report',
    children: [
      // {
      //   id: 'Project',
      //   title: 'Project',
      //   type: 'item',
      //   url: '/vms/reports/home',
      //   // role: ['Admin', 'Sales Manager', 'Sales Head']
      //   module: 'reports', permission: "project reports",
      // },
      {
        id: 'visitorhistory',
        title: 'Visitor History',
        type: 'item',
        url: '/vms/reports/visitorhistory',
        // role: ['Admin', 'Sales Manager', 'Sales Head', 'GRE'],
        module: 'report', permission: 'visitor history',
      },
      // {
      //   id: 'Visitors',
      //   title: 'Visitors Logs',
      //   type: 'item',
      //   url: '/vms/visitors/home',
      //   // icon: icons.visitors,
      //   // role: ['Admin', 'Security', 'Sales Manager', 'Sales Head', 'GRE']
      //   module: 'visitors', permission: 'list',
      // },
      {
        id: 'projectreport',
        title: 'Project',
        type: 'item',
        url: '/vms/reports/projectreport',
        // icon: icons.visitors,
        // role: ['Admin', 'Security', 'Sales Manager', 'Sales Head', 'GRE']
        module: 'report', permission: 'project',
      },
      // {
      //   id: 'TodayExpectedCustomers',
      //   title: "Today's Expected Vistors",
      //   type: 'item',
      //   url: '/vms/reports/todayexpected',
      //   // role: ['Admin', 'Sales Manager', 'Sales Head']
      //   module: 'reports', permission: "today's expected visitors",
      // },
      {
        id: 'crossselling',
        title: "Cross Selling",
        type: 'item',
        url: '/vms/reports/cross-selling',
        // role: ['Admin', 'Sales Manager', 'Sales Head', 'GRE'],
        module: 'report', permission: "cross selling",
      },
      {
        id: 'visitorbysource',
        title: "Visitors by Source",
        type: 'item',
        url: '/vms/reports/visitorsource',
        // role: ['Admin', 'Sales Manager', 'Sales Head', 'GRE'],
        module: 'report', permission: "visitors by source",
      },
      {
        id: 'cpbysource',
        title: "Visitors by CP",
        type: 'item',
        url: '/vms/reports/cp-source',
        // role: ['Admin', 'Sales Manager', 'Sales Head', 'GRE'],
        module: 'report', permission: "visitors by cp",
      },
      {
        id: 'schedulevisit',
        title: "Schedule Visit",
        type: 'item',
        url: '/vms/reports/schedule-visit',
        // role: ['Admin', 'Sales Manager', 'Sales Head', 'GRE'],
        module: 'report', permission: "schedule visit",
      },
      {
        id: 'smperformance',
        title: "SM Performance",
        type: 'item',
        url: '/vms/reports/sm-performance',
        // role: ['Admin', 'Sales Manager', 'Sales Head', 'GRE'],
        module: 'report', permission: "sm performance",
      },
      
    ]
  },
  // {
  //   id: 'user-management',
  //   title: 'User Management',
  //   type: 'item',
  //   url: '/vms/usermanagement/home',
  //   icon: icons.UserManagement,
  //   // role: ['Admin', 'GRE']
  //   module: 'user', permission: 'list',

  // },
  {
    id: 'usermanagement',
    title: 'User Management',
    type: 'collapse',
    icon: icons.UserManagement,
    // module: 'user',
    children: [
      { id: 'users', title: 'Users', type: 'item', url: '/vms/usermanagement/home', module: 'user', permission: 'summary' },
      { id: 'rolepermission', title: 'Role Permission', type: 'item', url: '/vms/usermanagement/rolepermission', module: 'role', permission: 'summary' },
      { id: 'rolesummary', title: 'Role Summary', type: 'item', url: '/vms/usermanagement/rolesummary', module: 'role', permission: 'summary' },
    ]
  },
  // {
  //   id: 'logs',
  //   title: 'Logs',
  //   type: 'item',
  //   url: '/vms/logs',
  //   icon: icons.Logs,
  //   role: ['Admin', 'Sales Manager', 'Sales Head']
  // }
];

// Grouped sidebar used only when a role holds BOTH Visitor Office and Front Desk access at once
// (hasBothOfficeTypes — today that's effectively just Admin). Reuses the exact same module/
// permission gating as the items above (so filterMenuItems doesn't need any special-casing), just
// re-parented into "Visitor MS" / "Front Desk" / "Administrator" so the two office types' Master/
// Reports sections read as clearly separate instead of needing the disambiguatedTitle prefixes.
// Every other role keeps the flat allMenuItems list above, completely unchanged.
const ADMIN_MENU_GROUPS = [
  {
    id: 'Dashboard',
    title: 'Dashboard',
    type: 'item',
    url: '/vms/dashboard/',
    icon: icons.dashbaord,
    module: 'dashboard',
    permission: [
      'total customer',
      'unique customer',
      're-visited customer',
      'expected customer',
      'cross selling',
      'visitor type',
      'visited by project',
      'walk-in form status',
      'feedback form status',
      'cross selling by project',
      'project based bhk preference',
      'project based feedback',
      'visitors attended by sm',
      'top sm - table',
      're-assigned list',
      'peak visiting hours',
      'sales manager performance',
      'tomorrow expected',
      'visitor interest status',
      "today's anticipated",
      'project wise performance'
    ]
  },
  {
    id: 'visitorMS',
    title: 'Visitor MS',
    type: 'collapse',
    icon: icons.visitors,
    children: [
      { id: 'visitorMS-summary', title: 'Visitor Summary', type: 'item', url: '/vms/visitorlist', module: 'customer', permission: 'summary' },
      {
        id: 'visitorMS-smmanagement',
        title: 'SM Management',
        type: 'collapse',
        children: [
          {
            id: 'visitorMS-smassign',
            title: 'SM Assinged',
            type: 'item',
            url: '/vms/gre/smassign',
            module: 'sm active',
            permission: 'assign summary'
          }
        ]
      },
      {
        id: 'visitorMS-master',
        title: 'Master',
        type: 'collapse',
        children: [
          { id: 'visitorMS-vendor', title: 'Vendor', type: 'item', url: '/vms/master/vendor', module: 'vendor', permission: 'summary' },
          { id: 'visitorMS-interest', title: 'Interest', type: 'item', url: '/vms/master/interest', module: 'interest', permission: 'summary' },
          { id: 'visitorMS-source', title: 'Source', type: 'item', url: '/vms/master/source', module: 'source type', permission: 'summary' },
          { id: 'visitorMS-vertical', title: 'Verticals', type: 'item', url: '/vms/master/vertical-master', module: 'source type', permission: 'summary' }
        ]
      },
      {
        id: 'visitorMS-reports',
        title: 'Reports',
        type: 'collapse',
        children: [
          {
            id: 'visitorMS-visitorhistory',
            title: 'Visitor History',
            type: 'item',
            url: '/vms/reports/visitorhistory',
            module: 'report',
            permission: 'visitor history'
          },
          {
            id: 'visitorMS-projectreport',
            title: 'Project',
            type: 'item',
            url: '/vms/reports/projectreport',
            module: 'report',
            permission: 'project'
          },
          {
            id: 'visitorMS-crossselling',
            title: 'Cross Selling',
            type: 'item',
            url: '/vms/reports/cross-selling',
            module: 'report',
            permission: 'cross selling'
          },
          {
            id: 'visitorMS-visitorbysource',
            title: 'Visitors by Source',
            type: 'item',
            url: '/vms/reports/visitorsource',
            module: 'report',
            permission: 'visitors by source'
          },
          {
            id: 'visitorMS-cpbysource',
            title: 'Visitors by CP',
            type: 'item',
            url: '/vms/reports/cp-source',
            module: 'report',
            permission: 'visitors by cp'
          },
          {
            id: 'visitorMS-schedulevisit',
            title: 'Schedule Visit',
            type: 'item',
            url: '/vms/reports/schedule-visit',
            module: 'report',
            permission: 'schedule visit'
          },
          {
            id: 'visitorMS-smperformance',
            title: 'SM Performance',
            type: 'item',
            url: '/vms/reports/sm-performance',
            module: 'report',
            permission: 'sm performance'
          }
        ]
      }
    ]
  },
  {
    id: 'frontDesk',
    title: 'Front Desk',
    type: 'collapse',
    icon: icons.master,
    children: [
      {
        id: 'frontDesk-summary',
        title: 'Summary',
        type: 'item',
        url: '/vms/checkin/frontdesk/summary',
        module: 'visitor/courier',
        permission: 'summary'
      },
      {
        id: 'frontDesk-master',
        title: 'Master',
        type: 'collapse',
        children: [
          {
            id: 'frontDesk-department',
            title: 'Department',
            type: 'item',
            url: '/vms/master/departments',
            module: 'department',
            permission: 'summary'
          },
          { id: 'frontDesk-office', title: 'Office', type: 'item', url: '/vms/master/office', module: 'office', permission: 'summary' },
          {
            id: 'frontDesk-employee',
            title: 'Employee',
            type: 'item',
            url: '/vms/master/employees',
            module: 'employee',
            permission: 'summary'
          },
          {
            id: 'frontDesk-idproof',
            title: 'ID Proof',
            type: 'item',
            url: '/vms/master/id-proof',
            module: 'id proof type',
            permission: 'summary'
          },
          {
            id: 'frontDesk-purposeofvisit',
            title: 'Purpose of Visit',
            type: 'item',
            url: '/vms/master/purpose-of-visit',
            module: 'purpose of visit',
            permission: 'summary'
          }
        ]
      },
      {
        id: 'frontDesk-reports',
        title: 'Reports',
        type: 'collapse',
        children: [
          {
            id: 'frontDesk-visitor-report',
            title: 'Visitor',
            type: 'item',
            url: '/vms/report/visitor',
            module: 'report',
            permission: 'visitor'
          },
          {
            id: 'frontDesk-department-report',
            title: 'Department',
            type: 'item',
            url: '/vms/report/department',
            module: 'report',
            permission: 'department'
          }
        ]
      }
    ]
  },
  {
    id: 'administrator',
    title: 'Administrator',
    type: 'collapse',
    icon: icons.UserManagement,
    children: [
      { id: 'administrator-users', title: 'Users', type: 'item', url: '/vms/usermanagement/home', module: 'user', permission: 'summary' },
      {
        id: 'administrator-rolepermission',
        title: 'Role Permission',
        type: 'item',
        url: '/vms/usermanagement/rolepermission',
        module: 'role',
        permission: 'summary'
      },
      {
        id: 'administrator-rolesummary',
        title: 'Role Summary',
        type: 'item',
        url: '/vms/usermanagement/rolesummary',
        module: 'role',
        permission: 'summary'
      }
    ]
  }
];

const Navigation = () => {
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));
  const { menuOrientation } = useConfig();
  const { drawerOpen } = useSelector((state) => state.menu);
  const [selectedItems, setSelectedItems] = useState('');
  const [selectedLevel, setSelectedLevel] = useState(0);
  const [menuItems, setMenuItems] = useState({ items: [] });

  // useEffect(() => {
  //   const userRole = localStorage.getItem('role');
  //   if (userRole) {
  //     const filterMenuItems = (items) => {
  //       return items
  //         .filter(
  //           (item) =>
  //             item.role.includes(userRole) || // Check if the current user role is in the role array
  //             (item.children && item.children.some((child) => child.role.includes(userRole))) // Check if the children have role access
  //         )
  //         .map((item) => {
  //           if (item.children) {
  //             return {
  //               ...item,
  //               children: item.children.filter((child) => child.role.includes(userRole)) // Filter children based on role
  //             };
  //           }
  //           return item;
  //         });
  //     };

  //     const filteredItems = filterMenuItems(allMenuItems);

  //     const vms = {
  //       id: 'group-vms',
  //       icon: icons.widgets,
  //       type: 'group',
  //       children: filteredItems
  //     };
  //     setMenuItems({ items: [vms] });
  //   }
  // }, []);
  // useEffect(() => {
  //   const userRole = localStorage.getItem('role');
  //   const permissions = JSON.parse(localStorage.getItem('permissions')) || []; // 🆕 dynamic permissions

  //   if (userRole) {
  //     const hasModulePermission = (module, permission) => {
  //       return permissions.some(
  //         (mod) =>
  //           mod.module_name === module &&
  //           mod.permissions?.includes(permission)
  //       );
  //     };

  //     const filterMenuItems = (items) => {
  //       return items
  //         .filter((item) => {
  //           // 🆕 MANUAL ROLE CHECK (safe)
  //           const hasRoleAccess =
  //             item.role?.includes(userRole) ?? false;

  //           // 🆕 DYNAMIC MODULE PERMISSION CHECK
  //           const hasPermissionAccess =
  //             item.module && item.permission
  //               ? hasModulePermission(item.module, item.permission)
  //               : false;

  //           // 🆕 CHILD CHECK (both role + permission)
  //           const hasChildAccess =
  //             item.children &&
  //             item.children.some((child) =>
  //               child.role?.includes(userRole) ||
  //               (child.module &&
  //                 child.permission &&
  //                 hasModulePermission(child.module, child.permission))
  //             );

  //           return hasRoleAccess || hasPermissionAccess || hasChildAccess;
  //         })
  //         .map((item) => {
  //           if (item.children) {
  //             return {
  //               ...item,
  //               children: item.children.filter(
  //                 (child) =>
  //                   child.role?.includes(userRole) || // 🆕 safe role check
  //                   (child.module &&
  //                     child.permission &&
  //                     hasModulePermission(child.module, child.permission)) // 🆕 permission check
  //               )
  //             };
  //           }
  //           return item;
  //         });
  //     };

  //     const filteredItems = filterMenuItems(allMenuItems);

  //     const vms = {
  //       id: 'group-vms',
  //       icon: icons.widgets,
  //       type: 'group',
  //       children: filteredItems
  //     };

  //     setMenuItems({ items: [vms] });
  //   }
  // }, []);


  const permissions = useSelector((state) => state.auth.permissions);
  // Normalized lowercase since the backend doesn't always send the role string with consistent
  // casing ("Sales Manager" vs "sales manager", "GRE" vs "gre") — compared against item.role
  // arrays below case-insensitively too, so a casing difference doesn't hide a menu item.
  const role = localStorage.getItem('role')?.trim().toLowerCase(); // get logged in user role
  console.log('🟢 User Role:', role);
  console.log('🟢 Backend Permissions:', permissions);

  // useEffect(() => {
  //   if (!permissions || !Array.isArray(permissions)) return;
  //   const filterByPermission = (items) => {
  //     return items
  //       .map((item) => {
  //         if (item.children) {
  //           const validChildren = filterByPermission(item.children);
  //           return validChildren.length > 0 ? { ...item, children: validChildren } : null;
  //         }

  //         const moduleData = permissions.find((mod) => mod.module_name === item.module);
  //         return moduleData?.permissions?.some((perm) => perm.name === item.permission)
  //           ? item
  //           : null;
  //       })
  //       .filter(Boolean);
  //   };
  //   const filteredItems = filterByPermission(allMenuItems);
  //   const vms = {
  //     id: 'group-vms',
  //     type: 'group',
  //     children: filteredItems
  //   };
  //   setMenuItems({ items: [vms] });
  // }, [permissions]);

  useEffect(() => {
    if (!Array.isArray(permissions)) return;

    // ✅ CHECK DYNAMIC PERMISSION — `permission` may be a single string or an array (matching
    // PermissionGuard's own string-or-array support), so a menu item can show for a user holding
    // ANY ONE of several permission names instead of just one exact match.
    const hasPermission = (module, permission) => {
      const moduleData = permissions.find((mod) => mod.module_name === module);
      if (!moduleData) return false;
      const allowedPermissions = moduleData.permissions?.map((perm) => perm.name) || [];
      return Array.isArray(permission)
        ? permission.some((perm) => allowedPermissions.includes(perm))
        : allowedPermissions.includes(permission);
    };

    // A role only needs the "Front Desk" / "Visitor Office" prefix on its Summary/Master/Reports
    // groups (flat nav) — or the fully grouped Admin sidebar below — when it actually holds BOTH
    // office types' access at once (e.g. Admin). A Receptionist only ever sees the Front Desk set,
    // so the plain flat labels/items stay for them. See each item's `disambiguatedTitle` above.
    const bothOfficeTypesVisible = hasBothOfficeTypes(permissions);

    // 🔴 UPDATED FILTER LOGIC
    const filterMenuItems = (items) => {
      return items
        .map((item) => {
          // 🔴 1. MANUAL ROLE CHECK (Dashboard case)
          const hasRoleAccess =
            item.role?.some((allowedRole) => allowedRole.trim().toLowerCase() === role) ?? false;

          // 🔴 2. DYNAMIC MODULE + PERMISSION CHECK
          const hasPermissionAccess =
            item.module && item.permission
              ? hasPermission(item.module, item.permission)
              : false;

          // 🔴 3. CHILD ACCESS CHECK (Recursive)
          let validChildren = [];
          if (item.children) {
            validChildren = filterMenuItems(item.children);
          }

          // 🔴 4. FINAL VISIBILITY CONDITION
          if (hasRoleAccess || hasPermissionAccess || validChildren.length > 0) {
            return {
              ...item,
              title: bothOfficeTypesVisible && item.disambiguatedTitle ? item.disambiguatedTitle : item.title,
              ...(item.children && { children: validChildren })
            };
          }

          return null;
        })
        .filter(Boolean);
    };

    // Grouped Admin-style sidebar (Visitor MS / Front Desk / Administrator) only when both office
    // types are visible — every other role keeps today's flat allMenuItems list untouched.
    const filteredItems = filterMenuItems(bothOfficeTypesVisible ? ADMIN_MENU_GROUPS : allMenuItems);

    const vms = {
      id: 'group-vms',
      type: 'group',
      children: filteredItems
    };

    setMenuItems({ items: [vms] });
  }, [permissions, role]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  let lastItemIndex = menuItems.items.length - 1;
  let remItems = [];
  let lastItemId;

  if (lastItem && lastItem < menuItems.items.length) {
    lastItemId = menuItems.items[lastItem - 1].id;
    lastItemIndex = lastItem - 1;
    remItems = menuItems.items.slice(lastItem - 1, menuItems.items.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon
    }));
  }

  const renderMenuItems = (items) => {
    return items.map((item) => {
      if (item.type === 'group') {
        return (
          <NavGroup
            key={item.id}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedItems={selectedItems}
            lastItem={lastItem}
            remItems={remItems}
            lastItemId={lastItemId}
            item={item}
          />
        );
      } else if (item.type === 'collapse') {
        return (
          <Box key={item.id} sx={{ ml: 2 }}>
            <Typography variant="h6" color="inherit" sx={{ color: '#ffffff', '&:hover': { color: '#C79A42' }, '&.active': { color: '#C79A42', borderLeft: '3px solid #C79A42' } }}>
              {item.title}
            </Typography>
            <Box>{renderMenuItems(item.children)}</Box>
          </Box>
        );
      } else {
        return (
          <Typography key={item.id} variant="h6" color="inherit" sx={{ color: '#ffffff', '&:hover': { color: '#C79A42' }, '&.active': { color: '#C79A42', borderLeft: '3px solid #C79A42' } }}>
            {item.title}
          </Typography>
        );
      }
    });
  };

  const navGroups = renderMenuItems(menuItems.items.slice(0, lastItemIndex + 1));

  return (
    <Box
      sx={{
        pt: drawerOpen ? (isHorizontal ? 0 : 2) : 0,
        '& > ul:first-of-type': { mt: 0 },
        display: isHorizontal ? { xs: 'block', lg: 'flex', xl: 'flex' } : 'block'
      }}
    >
      {navGroups}
    </Box>
  );
};

export default Navigation;
